import type { CallRecord } from '~/types/calls';

// Re-export for convenience so consumers can import from a single place
export type { CallRecord };

export type CallRecordDateGroup = {
  dateLabel: string;
  dayStartEpoch: number;
  calls: CallRecord[];
};

export function getCallStartEpoch(call: Pick<CallRecord, 'startEpoch'>): number {
  return Number.isFinite(call.startEpoch) && call.startEpoch > 0
    ? call.startEpoch
    : Number.NEGATIVE_INFINITY;
}

export function hasValidCallStartEpoch(call: Pick<CallRecord, 'startEpoch'>): boolean {
  return Number.isFinite(call.startEpoch) && call.startEpoch > 0;
}

export function isCallAnswered(call: CallRecord): boolean {
  return (call.billSec ?? 0) > 0;
}

export function isCallMissed(call: CallRecord): boolean {
  return !isCallAnswered(call) && isCallInbound(call);
}

export function isCallInbound(call: CallRecord): boolean {
  return call.bpCallDirection === 'inbound';
}

export function isCallOutbound(call: CallRecord): boolean {
  return call.bpCallDirection === 'outbound';
}

export function groupCallRecordsByDate(records: CallRecord[]): CallRecordDateGroup[] {
  const groups = new Map<number, CallRecordDateGroup>();

  for (const call of records) {
    const isValid = hasValidCallStartEpoch(call);
    const callDate = isValid ? new Date(call.startEpoch * 1000) : null;
    const dayStartEpoch = callDate
      ? Math.floor(
          new Date(
            callDate.getFullYear(),
            callDate.getMonth(),
            callDate.getDate(),
          ).getTime() / 1000,
        )
      : Number.NEGATIVE_INFINITY;

    let group = groups.get(dayStartEpoch);
    if (!group) {
      group = {
        dayStartEpoch,
        dateLabel: callDate
          ? callDate.toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })
          : 'Invalid Date',
        calls: [],
      };
      groups.set(dayStartEpoch, group);
    }

    group.calls.push(call);
  }

  return Array.from(groups.values())
    .sort((left, right) => right.dayStartEpoch - left.dayStartEpoch)
    .map((group) => ({
      ...group,
      calls: [...group.calls].sort(
        (left, right) => getCallStartEpoch(right) - getCallStartEpoch(left),
      ),
    }));
}

/**
 * Classifies a call outcome from its hangup cause string.
 * Returns 'answered', 'missed', or 'unknown' for unexpected/unhandled causes.
 */
export function getCallOutcome(hangupCause: string | null | undefined): 'answered' | 'missed' | 'unknown' {
  if (!hangupCause) return 'unknown';
  const cause = hangupCause.toUpperCase();
  if (cause === 'NORMAL_CLEARING') return 'answered';
  if (cause === 'NO_ANSWER' || cause === 'USER_BUSY' || cause === 'ORIGINATOR_CANCEL') return 'missed';
  return 'unknown';
}
